import selectn        from 'selectn'
import { common }     from './common'
import dateFormat     from '../../utils/dateformat'
import translate      from '../../utils/translate'

/* Helpers need to be moved to utils but for now until merged it will be kept here */
const helpers = {

  /**
  * Take in dates as strings and extract years to return year string
  *
  * @param  {Object}    edition     Edition config
  * @param  {Object}    params      Router params
  * @param  {Object}    location    Router location
  * @return {boolean}               Merged super object for ease of transportation
  **/
  requestObj: function ( edition = {}, params = {}, location = {} ) {
    return {
      edition : edition,
      params : params,
      location : location
    }
  },

  numberWithCommas: function ( x ) {
    const parts = x.toString().split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return parts.join('.')
  },

  getOrdinal: function ( n ) {
    const s = [ 'th', 'st', 'nd', 'rd' ]
    const v = n%100
    return n+(s[(v-20)%10]||s[v]||s[0])
  }
}

const schedule = {

  /**
  * Process data from APIs
  *
  * @param  {Object}  scheduleRes     API response from standings API
  * @param  {Object}  leagueRes       API response from league API
  * @param  {String}  displayDate     ISO Date of the data ( the action has to make decisions and get a default which can be based on data or params )
  * @param  {Object}  edition         Edition config
  * @param  {Object}  params          Router params
  * @param  {Object}  location        Router location
  * @param  {Object}  appProps        Application properties
  * @return {Object}                  Standings, seasons, current-season & league-title
  **/
  processData: function (scheduleRes = {}, leagueRes = {}, displayDate = '', edition = {}, params = {}, location = {}) {

    const request         = helpers.requestObj( edition, params, location ) /* until such time as we have 1 var i will fake it */
    const groupData       = schedule.getGroups( request, params.sport, params.league ? true : false, selectn( 'data', scheduleRes ) || {} )
    const leagueWhiteList = groupData.leagues || {}
    const metaData        = schedule.getMetaData( edition, request, selectn( 'data', scheduleRes) || {}, leagueWhiteList, location ) || {}
    const pageTitle       = metaData.pageTitle || ''

    const scheduleData  = {
      groups :      groupData.groups || {},
      config :      schedule.getConfig( params.sport, params.league, params.team, edition ),
      leagues :     schedule.filterLeagues( edition, request, selectn( 'data.leagues', leagueRes ) || [], params.team ? leagueWhiteList : null ),
      seasons :     schedule.getSeasons( request, edition ),
      activeDates : schedule.getActiveDates( selectn( 'data', scheduleRes ) || {} ),
      title :       pageTitle,
      date :        displayDate,
      sport :       params.sport,
      noGames :     translate( edition, 'No fixtures available' ),
      viewTodayText :     translate( edition, 'ViewToday' ),
      meta :        metaData,
      analytics :   schedule.getAnalytics( metaData ),
      teamFixtures :params.team ? true : false
    }

    return scheduleData
  },

  /**
  * Builds meta data object recognised by 'updateMeta' function
  * @param    {Object}  edition       Edition Object
  * @param    {Object}  request       Request Object
  * @param    {Object}  scheduleData  Schedule Data
  * @param    {Object}  leagueData    League Data
  * @param    {Object}  location      Router location
  * @return   {Object}                Object
  **/
  getMetaData: function ( edition = {}, request = {}, scheduleData = {}, leagueData = {}, location ) {

    const reqParams = selectn( 'params', request ) || {}
    const sport     = reqParams.sport || ''
    const league    = reqParams.league || ''
    const team      = reqParams.team || ''
    const season    = reqParams.season || ''
    const pageType  = reqParams.pageType || ''
    let title       = translate( edition, 'Fixtures and Results' )
    let subTitle    = translate( edition, 'Rugby' )
    let subTitle2   = translate( edition, 'fixtures and results' )
    let keywords    = [ 'rugby', 'rugby union', 'scores', 'news', 'schedule' ]
    let pageHeading = []
    let logo = ''
    let description = `Get the latest ${subTitle} ${subTitle2} from ESPN.`

    if ( sport == 'nrl' ) {
      subTitle    = translate( edition, 'NRL' )
      keywords    = [ 'rugby', 'NRL', 'scores', 'news', 'schedule' ]
    }

    if ( team ) {
      let teamName = schedule.getTeamTitle( request, scheduleData ) || ''
      let competitors = selectn( 'scores[0].events[0].competitions[0].competitors', scheduleData ) || []

      for (var i = 0; i < competitors.length; i++) {
        let competitor = competitors[i]
        if (competitor.id == team) {
          logo = selectn('team.logo', competitor) || common.defaultUrls.teamLogo
          logo = common.resizeCombinerLogo(logo,{ w:100, h:100 })
        }
      }

      if ( teamName ) {
        if ( pageType === 'results' ) {
          title = 'Results'
          keywords.push( 'results' )
          subTitle2   = 'results'
        } else {
          title = 'Fixtures'
          keywords.push( 'fixtures' )
          subTitle2   = 'fixtures'
        }
        pageHeading.push( teamName )
        subTitle = teamName
        keywords.push( 'highlights' )
        keywords.unshift(typeof teamName == 'string' && teamName.toLowerCase())
      }
    } else {
      if ( league ) {
        const leagueName = schedule.getLeagueTitle( request, leagueData ) || ''
        if ( leagueName ) {
          pageHeading.push( leagueName )
          subTitle = leagueName
        }
      } else {
        pageHeading.push( subTitle )
      }
      keywords.push( 'fixtures' )
      keywords.push( 'results' )
    }
    pageHeading.push( title )

    if ( season ) {
      pageHeading.push( '-' )
      pageHeading.push( season )
    }

    const mainTitle = pageHeading.join( ' ' )

    if ( sport === 'rugby' ) {
      title = ''
      keywords = ''
      description = ''
      if ( league ) {
        const leagueName = schedule.getLeagueTitle( request, leagueData ) || ''
        let transKeyObjs2 = {
          title : {
            key : 'meta.title.rugby.fixtures',
            league : leagueName
          },
          keywords: {
            key : 'meta.keywords.rugby.fixtures',
            league : leagueName
          },
          description: {
            key : 'meta.description.rugby.fixtures',
            league : leagueName
          }
        }
        title = translate(edition, transKeyObjs2.title) || ''
        description = translate(edition, transKeyObjs2.description) || ''
        keywords = translate(edition, transKeyObjs2.keywords) || ''
      } else if ( team ) {
        let teamName = schedule.getTeamTitle( request, scheduleData ) || ''
        let transKeyObjs2 = {
          title : {
            key : 'meta.title.rugby.fixtures',
            team : teamName
          },
          keywords: {
            key : 'meta.keywords.rugby.fixtures',
            team : teamName
          },
          description: {
            key : 'meta.description.rugby.fixtures',
            team : teamName
          }
        }
        title = translate(edition, transKeyObjs2.title) || ''
        description = translate(edition, transKeyObjs2.description) || ''
        keywords = translate(edition, transKeyObjs2.keywords) || ''
      } else {
        title = translate(edition, 'meta.title.rugby.fixtures') || ''
        description = translate(edition, 'meta.description.rugby.fixtures') || ''
        keywords = translate(edition, 'meta.keywords.rugby.fixtures') || ''
      }
    }

    return {
      title: title || `${mainTitle} - ESPN`,
      pageTitle: `${mainTitle}`,
      description: description,
      keywords: keywords || keywords.join( ', ' ),
      website: 'article',
      canonical: location.pathname,
      image: logo
    }
  },

  /**
  * Builds analytics data object recognised by 'analytics' function
  *
  * @param    {Object}  meta        Meta object returned from 'getMetaData'
  * @return   {Object}              Object
  **/
  getAnalytics: function ( meta ) {
    return {
      chartbeat: {
        title: meta.title
      },
      metrics: {}
    }
  },

  getActiveDates: function ( scheduleData ) {

    const scores  = selectn( 'scores', scheduleData) || []
    const scoreLen  = scores.length || 0
    let activeDates = {}

    for ( let i = 0; i < scoreLen; i++ ) {
      const calendarData = selectn( 'leagues[0].calendar', scores[i] ) || []
      const calLen = calendarData.length
      for ( let a = 0; a < calLen; a++ ) {
        const loopDateObj = dateFormat.dateToObject( calendarData[a] ) || new Date()
        const epochTime = loopDateObj.setHours(0,0,0,0)
        activeDates[epochTime] = 1
      }
    }

    return activeDates
  },

  getLeagueTitle: function ( request = {}, leagueData = {} ) {
    const reqParams = selectn( 'params', request ) || {}
    const league    = reqParams.league || ''
    return leagueData[ league ] || ''
  },

  getTeamTitle: function ( request = {}, scheduleData = {} ) {
    const reqParams = selectn( 'params', request ) || {}
    const team      = reqParams.team || ''
    let teamName = ''
    if ( team ) {
      const firstCompetitor = selectn( 'scores[0].events[0].competitions[0].competitors[0].team', scheduleData ) || {}
      if( firstCompetitor.id === team ) {
        teamName = firstCompetitor.displayName || ''
      } else {
        const secondCompetitor = selectn( 'scores[0].events[0].competitions[0].competitors[1].team', scheduleData ) || {}
        teamName = secondCompetitor.displayName || ''
      }
    }
    return teamName
  },

  getPageHeading: function ( request = {}, scheduleData = {}, leagueData = {} ) {

    const reqParams = selectn( 'params', request ) || {}
    const league    = reqParams.league || ''
    const team      = reqParams.team || ''
    const season    = reqParams.season || ''
    const pageType  = reqParams.pageType || ''
    let title       = 'Fixtures and Results'
    let pageHeading = []

    if ( team ) {
      let teamName = schedule.getTeamTitle( request, scheduleData ) || ''
      if ( teamName ) {
        if ( pageType === 'results' ) {
          title = 'Results'
        } else {
          title = 'Fixtures'
        }
        pageHeading.push( teamName )
      }
    }

    if ( league ) {
      const leagueName = schedule.getLeagueTitle( request, leagueData ) || ''
      if ( leagueName ) {
        pageHeading.push( leagueName )
      }
    } else {
      pageHeading.push( 'Rugby' )
    }

    pageHeading.push( title )

    if ( season ) {
      pageHeading.push( '-' )
      pageHeading.push( season )
    }

    return pageHeading.join( ' ' )
  },

  getConfig: function ( sport = '', league = '', team = '', edition={} ) {

    let config      = {}
    let commonKeys = {
      date : { key:'date', name: translate(edition, 'Date').toUpperCase() },
      matchup :{ key:'matchup', name: translate(edition, 'Match').toUpperCase() },
      time : { key:'time', name: translate(edition, 'time').toUpperCase() },
      tv: { key:'tv', name:'TV' },
      league:{ key:'league', name: translate(edition, 'Competition').toUpperCase() },
      location: { key:'location', name: translate(edition, 'location').toUpperCase() },
      result : { key:'result', name: translate(edition, 'result').toUpperCase() },
      attendance: { key:'attendance', name: translate(edition, 'Attendance').toUpperCase() }
    }
    if ( league === 'nba' ) {

      config = {
        'states':{
          'pre':[
            { key:'matchup', name:'MATCHUP' },
            { key:'time', name:'TIME' },
            { key:'tv', name:'NAT TV' },
            { key:'awayTv', name:'AWAY TV' },
            { key:'homeTv', name:'Home TV' },
            { key:'tickets', name:'TICKETS' }
          ],
          'post':[
            { key:'matchup', name:'MATCHUP' },
            { key:'result', name:'RESULT' },
            { key:'nbaWinnerLeader', name:'WINNER HIGH' },
            { key:'nbaLoserLeader', name:'LOSER HIGH' }
          ]
        },
        'cols':{ 'col1':'points', 'col2':'points' }
      }

    }else if ( league === 'mlb' ) {

      config = {
        'states':{
          'pre':[
            { key:'matchup', name:'MATCHUP' },
            { key:'time', name:'TIME' },
            { key:'tv', name:'NAT TV' },
            { key:'pitchingMatchup', name:'PITCHING MATCHUP' },
            { key:'tickets', name:'TICKETS' }
          ],
          'post':[
            { key:'matchup', name:'MATCHUP' },
            { key:'result', name:'RESULT' },
            { key:'pitchLeader', name:'WIN' },
            { key:'pitchLoser', name:'LOSS' },
            { key:'pitchSaver', name:'SAVE' }
          ]
        }
      }

    }else if ( sport === 'soccer' ) {

      let oSoccerPre = [
        { key:'matchup', name:'MATCH' },
        { key:'time', name:'TIME' },
        { key:'tv', name:'TV' },
        { key:'betting', name:'BETTING' }
      ]

      if ( league === 'usa.1' ) {
        oSoccerPre = [
          { key:'matchup', name:'MATCH' },
          { key:'time', name:'TIME' },
          { key:'tv', name:'TV' },
          { key:'location', name:'LOCATION' }
        ]
      }

      config = {
        'states':{
          'pre':oSoccerPre,
          'post':[
            { key:'matchup', name:'MATCH' },
            { key:'result', name:'RESULT' },
            { key:'location', name:'LOCATION' },
            { key:'attendance', name:'ATTENDANCE' }
          ]
        }
      }

    }else if ( league === 'nfl' ) {

      config = {
        'states':{
          'pre':[
            { key:'matchup', name:'MATCHUP' },
            { key:'time', name:'TIME' },
            { key:'tv', name:'NAT TV' },
            { key:'location', name:'LOCATION' },
            { key:'tickets', name:'TICKETS' }
          ],
          'post':[
            { key:'matchup', name:'MATCHUP' },
            { key:'result', name:'RESULT' },
            { key:'passingLeader', name:'PASSING LEADER' },
            { key:'rushingLeader', name:'RUSHING LEADER' },
            { key:'receivingLeader', name:'RECEIVING LEADER' }
          ]
        }
      }

    }else if ( sport === 'rugby' ||  sport === 'nrl' ) {

      if( team ) {
        config = {
          'states':{
            'pre':[ commonKeys.date, commonKeys.matchup, commonKeys.time, commonKeys.league, commonKeys.location ],

            'post':[ commonKeys.date, commonKeys.matchup, commonKeys.result, commonKeys.league, commonKeys.location ]
          }
        }
      } else {
        config = {
          'states':{
            'pre':[ commonKeys.matchup, commonKeys.time, commonKeys.tv, commonKeys.location ],
            'post':[ commonKeys.matchup, commonKeys.result, commonKeys.location, commonKeys.attendance ]
          },
          'sportClass' : 'soccer'
        }
      }
    }

    return config
  },

  getGroups: function ( request = {}, sport = '', showDate = false, scheduleData = {} ) {

    const scores    = selectn( 'scores', scheduleData) || []
    const scoreLen  = scores.length || 0
    let scoreData   = []
    let leagueMap   = {}

    for ( let i = 0; i < scoreLen; i++ ) {
      const leagueData  = scores[i] || {}
      const eventData   = selectn( 'events', leagueData ) || []
      const leagueInfo  = selectn( 'leagues[0]', leagueData ) || {}
      const leagueName  = schedule.getLeagueName( leagueInfo )
      const leagueId    = schedule.getLeagueId( leagueInfo )

      leagueMap[leagueId] = leagueName
      if (eventData.length) {
        scoreData.push({
          groupName :   schedule.getGroupName( showDate, leagueData ),
          ...schedule.getEvents( request, sport, leagueId, eventData, leagueName )
        })
      }
    }

    if( selectn( 'params.team', request) ) {
      return schedule.groupByMonth( request, scoreData )
    }

    return {
      groups : scoreData,
      leagues : leagueMap
    }
  },

  /**
  * Reorder the groups and insert the events into months, This will also
  * filter the events based on league and season
  * @param  {Object}  request       Unified request data object
  * @param  {Object}  scheduleData  Output from getGroups ( grouped events )
  * @return {Object}                Array of objects ( grouped events )
  **/
  groupByMonth: function ( request = {}, scheduleData = {} ) {

    const dataLen   = scheduleData.length || 0
    const season    = selectn( 'params.season', request) || new Date().getFullYear()
    const league    = selectn( 'params.league', request) || ''
    const pageType  = selectn( 'params.pageType', request) || ''
    let complete    = []
    let incomplete  = []
    const leagueMap = {}

    for ( let i = 0; i < dataLen; i++ ) {
      const groupData = scheduleData[i]
      complete    = complete.concat(groupData.complete)
      incomplete  = incomplete.concat(groupData.incomplete)
    }

    function compare( a, b ) {
      let aVal = selectn('date', a ) || 0
      let bVal = selectn('date', b ) || 0
      aVal = dateFormat.dateToKey( aVal )
      bVal = dateFormat.dateToKey( bVal )
      if (aVal < bVal) {
        return -1
      }
      if (aVal > bVal) {
        return 1
      }
      return 0
    }

    complete.sort( compare )
    incomplete.sort( compare )

    let sortedData      = []

    for ( let i = 0; i < 12; i++ ) {
      const date = season + '-' + ('0'+( i + 1 )).slice(-2) + '-01T00:00:00Z'
      sortedData.push({
        complete :    [],
        incomplete :  [],
        groupName :   dateFormat.dateFormat( date, 'MMMM, YYYY' )
      })
    }

    let events = complete
    let eventType = 'complete'

    if ( pageType !== 'results' ) {
      events = incomplete
      eventType = 'incomplete'
    }

    const completeLen   = events.length || 0
    for ( let i = 0; i < completeLen; i++ ) {
      const evtDate   = selectn('[' + i + '].date', events )
      const evtLeague = selectn('[' + i + '].leagueId', events )
      const month     = ( dateFormat.dateFormat( evtDate, 'MM' ) * 1 ) - 1
      const year      = dateFormat.dateFormat( evtDate, 'YYYY' ) * 1
      if( season == year ) {
        if ( league === '' || evtLeague == league ) {
          sortedData[month][eventType].push( events[i] )
        }
        leagueMap[evtLeague] = selectn('[' + i + '].league', events ) || evtLeague
      }
    }

    let returnData      = []
    for ( let i = 0; i < 12; i++ ) {
      if ( sortedData[i].complete.length > 0 || sortedData[i].incomplete.length > 0 ) {
        if ( pageType == 'results' && sortedData[i].complete.length > 0 ) {
          sortedData[i].complete.reverse()
        }
        returnData.push( sortedData[i] )
      }
    }

    if ( pageType == 'results' ) {
      returnData.reverse()
    }

    return {
      groups: returnData,
      leagues: leagueMap
    }

  },

  getGroupName: function ( showDate = false, leagueData = [] ) {
    if ( showDate ) {
      return dateFormat.dateFormat( selectn( 'events[0].date', leagueData ) || new Date(), 'date14' )
    }
    return schedule.getLeagueName( selectn( 'leagues[0]', leagueData ) ) || ''
  },

  getEvents: function ( request = {}, sport = '', leagueId = '', eventsData = [], leagueName = '' ) {

    const eventLen        = eventsData.length || 0
    const dateForm        = 'time1'
    const edition         = request && request.edition
    const lang            = edition && edition.language
    let processedEvents   = {
      incomplete :  [],
      complete :    []
    }

    for ( let i = 0; i < eventLen; i++ ) {

      const eventData   = eventsData[i]
      const competition = selectn( 'competitions[0]', eventData) || {} /* original code makes and assuption there is only 1 competition so i will do the same. */
      const competitors = selectn( 'competitors', competition) || {}
      const links       = selectn( 'links', eventData) || []
      const linkMap     = schedule.getLinkMap( links )
      const eventDate   = selectn( 'date', eventData) || ''
      const homeTeam    = schedule.getTeam( request, competitors, leagueId, 'home') || {}
      const awayTeam    = schedule.getTeam( request, competitors, leagueId, 'away') || {}
      const gameState   = selectn( 'status.type.state', eventData) || 'pre'
      const gameLink    = schedule.getEventLink( sport, gameState, linkMap )
      const period      = selectn( 'status.period', eventData) || 0
      const gameUrl     = common.getUrlByRel({ links }, 'summary', edition.domain, sport)


      let note          = ''
      let record        = 'v'
      let betLink       = {}

      /* build post game data */
      if (gameState === 'post') {

        if (sport === 'soccer' || sport === 'rugby' || sport === 'nrl') {

          var iLeg = selectn( 'leg.value', competition) || 0

          /* build the score */
          if (iLeg == 2 && homeTeam.aggregateScore != null &&  awayTeam.aggregateScore != null) {
            record = homeTeam.score+' ('+homeTeam.aggregateScore+') - ('+awayTeam.aggregateScore+') '+awayTeam.score
          } else {
            record = homeTeam.score+' - '+awayTeam.score
          }

          if (iLeg > 0) {

            if (note !== '') {
              note += ' - '
            }

            note += helpers.getOrdinal(iLeg) + ' Leg'

            /* build aggeragate winner text */
            if (gameState !== 'pre') {
              note += schedule.getSoccerNote( homeTeam, awayTeam, iLeg, 'aggregate', period )
            }
          }

          /* build penalties text */
          if (gameState !== 'pre' && (homeTeam.shootoutScore > 0 || awayTeam.shootoutScore > 0)) {
            note += schedule.getSoccerNote( homeTeam, awayTeam, iLeg, 'penalties', period )
          }

        }else{
          //console.log('damned lint')
        }

      }else{

        /* build the betting links */
        betLink = linkMap.bet365 || {}
        if (betLink) {
          betLink.href = '#'
          betLink.linkTrack = 'bet365'
          betLink.text = 'Football Odds with Bet365'
        }else{
          betLink.href = '#'
          betLink.text = ''
          betLink.linkTrack = ''
        }

      }

      const linkDefaults = {
        edition   : request.edition,
        href      : gameUrl,
        external  : gameLink.external,
        tracking  : [ leagueId, 'schedule', 'score' ]
      }

      const resultLink  = common.buildLinkObject({
        text :    selectn( 'status.type.shortDetail', eventData) || '',
        ...linkDefaults
      })

      const timeLink  = common.buildLinkObject({
        text :    gameState === 'in' ? 'LIVE' : '',
        ...linkDefaults
      })

      const recordLink  = common.buildLinkObject({
        text :    record,
        ...linkDefaults
      })

      //SEWEB-13325 hide team links for es/pt
      if(lang === 'es' || lang === 'pt') {
        homeTeam.link.href = null
        awayTeam.link.href = null
      }

      let processedEvent = {
        homeTeam :    homeTeam,
        awayTeam :    awayTeam,
        dateData :    eventDate,
        dateFormat :  dateForm,
        displayTime : timeLink,
        result :      resultLink,
        note :        note,
        gameId :      competition.id || 0,
        tv :          null,
        betting :     betLink,
        tickets :     null,
        soccerRecord :  recordLink,
        timeValid :   competition.timeValid  || false,
        gameState :   gameState,
        period :      period,
        attendance :  schedule.getAttendance( competition ),
        location :    schedule.getEventVenue( competition ),
        date :        eventDate,
        league :      leagueName,
        leagueId :    leagueId
      }


      if ( processedEvent.gameState !== 'post' ) {
        processedEvents.incomplete.push( processedEvent )
      }else{
        processedEvents.complete.push( processedEvent )
      }
    }

    return processedEvents

  },

  getAttendance: function ( competition = {} ) {
    let attendance = selectn('attendance',competition) || 0
    if (attendance > 0) {
      return helpers.numberWithCommas(competition.attendance)
    }
    return 'N/A'
  },

  getLinkMap: function ( links = [] ) {

    const linkLen = links.length
    let rLinkMap  = {}

    for ( let i = 0; i <  linkLen; i++ ) {
      const linkData       = links[i]
      const sLinkKey    = linkData.text.toLowerCase().replace(' ','')

      rLinkMap[sLinkKey]= {
        href :    linkData.href || '#',
        text :    linkData.text || '',
        external :  linkData.isExternal || false
      }

    }


    return rLinkMap
  },

  getEventLink: function ( sport = '', gameState = 'pre', rLinkMap = {} ) {

    let linkHref  = {}
    const linkBlank = {
      href      : '#',
      external  : false,
      text      : '-'
    }

    /* Build the game links */
    let rLinkTypeMap = {
      default         : 'conversation',
      preLink         : 'preview',
      liveLink        : 'gamecast',
      secondLiveLink  : 'play-by-play',
      postLink        : 'recap',
      secondPostLink  : 'box score'
    }

    if (sport === 'soccer') {
      rLinkTypeMap = {
        default         : 'statistics',
        postLink        : 'report',
        secondLiveLink  : 'statistics',
        secondPostLink  : 'statistics'
      }
    }else if (sport === 'rugby' || sport === 'nrl' ) {
      rLinkTypeMap = {
        default         : 'match',
        postLink        : 'report',
        secondLiveLink  : 'statistics',
        secondPostLink  : 'statistics'
      }
    }

    if (gameState === 'pre') {
      linkHref = rLinkMap[ rLinkTypeMap['preLink'] ]  ? rLinkMap[ rLinkTypeMap['preLink'] ]   : rLinkMap[ rLinkTypeMap['default'] ]
    } else if (gameState === 'live') {
      linkHref = rLinkMap[ rLinkTypeMap['liveLink'] ] ? rLinkMap[ rLinkTypeMap['liveLink'] ]  : rLinkMap[ rLinkTypeMap['secondLiveLink'] ]
    } else if (gameState === 'post') {
      linkHref = rLinkMap[ rLinkTypeMap['postLink'] ] ? rLinkMap[ rLinkTypeMap['postLink'] ]  : rLinkMap[ rLinkTypeMap['secondPostLink'] ]
    }

    if ( !selectn( 'href', linkHref ) ) {
      linkHref = rLinkMap[ rLinkTypeMap['default'] ] ? rLinkMap[ rLinkTypeMap['default'] ] : linkBlank
    }

    if( selectn( 'href', linkHref ) !== '#' ) {
      linkHref.href = common.buildURL( linkHref.href )
    }

    return linkHref

  },

  getEventVenue: function ( competition = {}, league = '' ) {

    let venue       = selectn( 'venue.fullName', competition ) || ''
    const country   = selectn( 'venue.address.country', competition ) || null
    const city      = selectn( 'venue.address.city', competition ) || null

    if (city != null) {
      venue = venue + ', ' + city
    }

    if (country != null && league != 'usa.1') {
      venue = venue + ', ' + country
    }

    return venue

  },

  getSoccerNote: function ( homeTeam = {}, awayTeam = {}, leg = 1, sType = '', period = 0 ) {

    if ( leg <= 1 ) {
      return ''
    }

    const homeScore     = sType === 'aggregate' ? homeTeam.aggregateScore : homeTeam.shootoutScore
    const awayScore     = sType === 'aggregate' ? awayTeam.aggregateScore : awayTeam.shootoutScore
    let note            = ''
    let sResult         = ''
    let bTied           = false
    let sWillAdvance    = homeTeam.name
    //var sWonMatch       = homeTeam.name
    let sWinning        = null
    let iFirstScore     = homeScore
    let iSecondScore    = awayScore

    if ( awayScore > homeScore ) {
      sWinning = awayTeam.name
    }else if ( awayScore < homeScore ) {
      sWinning = homeTeam.name
    }

    if (awayTeam.willAdvance) {
      sWillAdvance  = awayTeam.name
      iFirstScore   = awayScore
      iSecondScore  = homeScore
    }

    //if (awayTeam.isWinner) {
    //    sWonMatch = awayTeam.name;
    //}

    sResult = sWillAdvance + ' win'
    if (homeScore === awayScore) {
      sResult = 'Tied'
      if (sType === 'aggregate' && period != 5) {
        bTied = true
      }
    }else if (sWinning != null && sWillAdvance !== sWinning) {
      sResult = sWinning + ' lead'
    }

    note = ' - ' + sResult + ' ' + iFirstScore + '-' + iSecondScore + ' on ' + sType

    if (bTied) {
      note += ' - ' + sWillAdvance + ' advances'
    }

    return note

  },

  getTeam: function ( request = {}, competitors = [], leagueId = '', homeAway = 'home' ) {

    let team      = {}
    const compLen = competitors.length

    for( let i = 0; i < compLen; i++ ) {
      const oCompetitor = competitors[i] || {}
      if (oCompetitor.homeAway === homeAway) {
        team                 = schedule.getTeamInformation( request, leagueId, oCompetitor.team )
        team.isWinner        = oCompetitor.winner || false
        team.willAdvance     = oCompetitor.advance || false
        team.score           = oCompetitor.score || 0
        team.aggregateScore  = oCompetitor.aggregateScore || 0
        team.shootoutScore   = oCompetitor.shootoutScore || 0
        return team
      }
    }

    return team
  },

  getTeamInformation: function ( request = {}, leagueId = 0, oTeamData = {} ) {

    let edition = request.edition
    let linkText = 'Clubhouse'
    if(edition.language === 'es' || edition.language === 'pt') {
      linkText = 'Sede'
    }

    const rTmpTeam = {
      id :        parseInt(oTeamData.id) || null,
      name :      oTeamData.name || null,
      abbrev :    oTeamData.abbreviation || null,
      displayName : oTeamData.displayName || null,
      hasLogo :   true,
      logo :  common.getTeamLogo( oTeamData || {}, { w:50, h:50 } ),
      link  : common.buildLinkObject({
        text        : oTeamData.name,
        href        : common.getTeamUrl( oTeamData || {}, linkText ) || '',
        query       : request.query || {},
        edition     : request.edition || {},
        tracking    : [ leagueId, 'schedule', 'team' ],
        classes     : [ 'team-name' ]
      }),
      isActive :  oTeamData.isActive || false,
      willAdvance : false,
      advance :   false,
      score :     null,
      aggregateScore :null,
      shootoutScore : null
    }

    return rTmpTeam
  },

  /**
  * Get a list of link objects for all valid leagues
  * @param  {Object}  edition   Edition object
  * @param  {Object}  request   Request object
  * @param  {Object}  data      Return value from leagues API (unmolested)
  * @param  {Object}  whitelist Object keys are leagueIds and values are league names
  * @return {Array}             valid leagues
  **/
  filterLeagues: function ( edition = {}, request = {}, data = [], whitelist = null ) {

    const reqParams     = selectn( 'params', request ) || {}
    const translateKey  = 'dropdown.leagues.default.' + reqParams.sport
    const leagueLen     = data.length || 0
    let selected = translate(edition, translateKey)
    if ( selected === translateKey ) {
      selected = translate(edition, 'dropdown.leagues.default.sport')
    }
    let leagues       	= []
    let defaultParams 	= {
      pageType :  reqParams.pageType || 'fixtures',
      sport :     reqParams.sport
    }

    if( reqParams.team ) {
      defaultParams.team = reqParams.team
    }
    if( reqParams.season ) {
      defaultParams.season = reqParams.season
    }

    leagues.push(common.buildLinkObject({
      text :    selected,
      params :  defaultParams,
      query :   request.query,
      edition   : request.edition,
      tracking : [ 'all', 'schedule', 'filter', 'league' ]
    }))

    for ( let i = 0; i < leagueLen; i++ ) {

      const league      = data[i]
      const leagueName  = schedule.getLeagueName( league ) || ''
      let leagueId      = selectn('midsizeName', league) || ''
      let slug          = ''

      if ( reqParams.sport === 'cricket' ) {
        leagueId  = selectn('id', league) || ''
        slug      = selectn('slug', league) || ''
      }

      if ( whitelist && !whitelist[leagueId] ) {
        continue
      }

      if (leagueId === reqParams.league) {
        selected = leagueName
      }

      leagues.push(common.buildLinkObject({
        text :    leagueName,
        params :  {
          ...defaultParams,
          league    : leagueId.toLowerCase(),
          slug      : slug
        },
        query     : request.query,
        edition   : request.edition,
        tracking  : [ leagueId, 'schedule', 'filter', 'league' ]
      }))

    }

    return {
      items :     leagues,
      selected :  selected
    }

  },

  /**
  * Get a list of link objects for the last 10 years
  * @param  {Object}    request   Request object
  * @param  {Object}    edition   edition object
  * @return {Array}               Array of link objects
  **/
  getSeasons: function ( request, edition ) {

    const reqParams   = selectn( 'params', request ) || {}
    const endYear     = new Date().getFullYear()
    const startYear   = endYear - 10
    const selected    = reqParams.season || endYear

    let seasons = []

    if( reqParams.team && reqParams.pageType === 'results' ) {

      for (let i = startYear; i <= endYear; i++) {

        let params = {
          pageType :  reqParams.pageType || 'fixtures',
          sport :     reqParams.sport,
          team :      reqParams.team,
          season :    i,
          slug :      reqParams.slug
        }

        seasons.push(common.buildLinkObject({
          text :    i,
          query :   request.query,
          params,
          tracking : [ reqParams.team, 'fixtures', 'filter', 'year' ],
          edition
        }))

      }
    }

    return {
      items :     seasons.reverse(),
      selected :  selected
    }

  },

  /**
  * Gets the league name from the data and formats accordingly
  * @param  {Object}  data      League Object
  * @return {String}            League title
  **/
  getLeagueName: function ( data = {} ) {
    return selectn( 'name', data ).trim() || ''
  },

  /**
  * Gets the league name from the data and formats accordingly
  * @param  {Object}  data      League Object
  * @param  {String}  sport     Sport name
  * @return {String}            League id
  **/
  getLeagueId: function ( data = {}, sport = '' ) {
    if ( sport === 'cricket' ) {
      return selectn( 'id', data ) || ''
    }
    return selectn( 'midsizeName', data ) || selectn( 'id', data ) || ''
  }
}

export default schedule



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/schedule.js